import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell, faMessage, faCheckDouble, faCakeCandles } from "@fortawesome/free-solid-svg-icons";
import { useNotifications } from "../Contexts/NotificationContext";
import { useNavigate } from "react-router-dom";

export default function NotificationPanel() {
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div style={{ position: "relative" }} ref={dropdownRef}>
      <div 
        onClick={() => setIsOpen(!isOpen)}
        style={{
          position: "relative",
          cursor: "pointer",
          color: "#64748b",
          fontSize: "18px",
          padding: "8px",
          borderRadius: "8px",
          transition: "background 0.2s",
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        onMouseOver={(e) => e.currentTarget.style.background = "#f1f5f9"}
        onMouseOut={(e) => e.currentTarget.style.background = "transparent"}
      >
        <FontAwesomeIcon icon={faBell} />
        {unreadCount > 0 && (
          <span style={{
            position: "absolute",
            top: "2px",
            right: "2px",
            background: "#ef4444",
            color: "#fff",
            fontSize: "10px",
            fontWeight: 800,
            padding: "2px 5px",
            borderRadius: "10px",
            border: "2px solid #fff",
            minWidth: "14px",
            textAlign: "center"
          }}>
            {unreadCount}
          </span>
        )}
      </div>

      {isOpen && (
        <div style={{
          position: "absolute",
          top: "100%",
          right: 0,
          marginTop: "12px",
          width: "320px",
          maxHeight: "450px",
          background: "#fff",
          borderRadius: "12px",
          boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
          border: "1px solid #e2e8f0",
          zIndex: 1000,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden"
        }}>
          <div style={{
            padding: "16px",
            borderBottom: "1px solid #f1f5f9",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: "#fff"
          }}>
            <span style={{ fontWeight: 700, fontSize: "15px", color: "#1e293b" }}>Notifications</span>
            {unreadCount > 0 && (
              <button 
                onClick={(e) => { e.stopPropagation(); markAllAsRead(); }}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#3b82f6",
                  fontSize: "12px",
                  fontWeight: 600,
                  cursor: "pointer",
                  padding: "4px 8px",
                  borderRadius: "4px"
                }}
              >
                Mark all read
              </button>
            )}
          </div>

          <div style={{
            flex: 1,
            overflowY: "auto",
            background: "#fff"
          }}>
            {notifications.length === 0 ? (
              <div style={{
                padding: "40px 20px",
                textAlign: "center",
                color: "#94a3b8",
                fontSize: "13px"
              }}>
                <div style={{ fontSize: "24px", marginBottom: "8px" }}>🔔</div>
                No notifications yet
              </div>
            ) : (
              notifications.map(n => (
                <div 
                  key={n.id} 
                  onClick={() => {
                    markAsRead(n.id);
                    if (n.link) navigate(n.link);
                    setIsOpen(false);
                  }}
                  style={{
                    padding: "12px 16px",
                    borderBottom: "1px solid #f8fafc",
                    cursor: "pointer",
                    display: "flex",
                    gap: "12px",
                    background: n.is_read ? "#fff" : "#f0f7ff",
                    transition: "background 0.2s"
                  }}
                  onMouseOver={(e) => e.currentTarget.style.background = n.is_read ? "#f8fafc" : "#e0efff"}
                  onMouseOut={(e) => e.currentTarget.style.background = n.is_read ? "#fff" : "#f0f7ff"}
                >
                  <div style={{
                    width: "32px",
                    height: "32px",
                    borderRadius: "50%",
                    background: n.notification_type === 'message' ? "#dcfce7" : n.notification_type === 'celebration' ? "#fff7ed" : "#eff6ff",
                    color: n.notification_type === 'message' ? "#15803d" : n.notification_type === 'celebration' ? "#f59e0b" : "#1d4ed8",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "14px",
                    flexShrink: 0
                  }}>
                    <FontAwesomeIcon icon={n.notification_type === 'message' ? faMessage : n.notification_type === 'celebration' ? faCakeCandles : faBell} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ 
                      fontWeight: 600, 
                      fontSize: "13px", 
                      color: "#1e293b",
                      marginBottom: "2px" 
                    }}>{n.title}</div>
                    <div style={{ 
                      fontSize: "12px", 
                      color: "#64748b",
                      lineHeight: "1.4",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden"
                    }}>{n.content}</div>
                    <div style={{ 
                      fontSize: "10px", 
                      color: "#94a3b8",
                      marginTop: "6px" 
                    }}>{new Date(n.created_at).toLocaleString()}</div>
                  </div>
                </div>
              ))
            )}
          </div>

          <div style={{
            padding: "10px",
            textAlign: "center",
            borderTop: "1px solid #f1f5f9",
            background: "#f8fafc"
          }}>
            <button 
              onClick={() => { setIsOpen(false); navigate('/dashboard'); }}
              style={{
                background: "transparent",
                border: "none",
                color: "#64748b",
                fontSize: "12px",
                fontWeight: 600,
                cursor: "pointer"
              }}
            >
              View All in Dashboard
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
